/**
 * <phototron-showcase> Web Component
 * 
 * Combined viewer that shows material preview and loads 3D model on demand.
 * Perfect for e-commerce product pages where bandwidth matters.
 * 
 * @example Click-to-3D mode (default)
 * ```html
 * <phototron-showcase
 *   asset-id="shoe-001"
 *   api-key="pk_live_xxxxx"
 *   mode="click-to-3d"
 *   width="600"
 *   height="400">
 * </phototron-showcase>
 * ```
 * 
 * @example Tabs mode
 * ```html
 * <phototron-showcase
 *   asset-id="shoe-001"
 *   api-key="pk_live_xxxxx"
 *   mode="tabs"
 *   width="600"
 *   height="400">
 * </phototron-showcase>
 * ```
 */

import { ApiClient } from '../core/api-client';
import { AssetData } from '../core/types';
import './material-viewer';
import './model-viewer';

type ShowcaseMode = 'click-to-3d' | 'tabs';

const TEMPLATE = document.createElement('template');
TEMPLATE.innerHTML = `
  <style>
    :host {
      display: block;
      position: relative;
      overflow: hidden;
    }
    
    .container {
      width: 100%;
      height: 100%;
      position: relative;
      background: var(--phototron-bg, #050505);
    }
    
    .viewer-wrapper {
      width: 100%;
      height: 100%;
      position: relative;
    }
    
    phototron-material,
    phototron-viewer {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      transition: opacity 0.3s ease;
    }
    
    .hidden {
      opacity: 0;
      pointer-events: none;
    }
    
    .click-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-end;
      padding-bottom: 18px;
      box-sizing: border-box;
      /* No dim, and transparent to the pointer so mouse-move still reaches the
         material canvas underneath (drives the relight). Only the button is
         interactive. */
      background: transparent;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.25s ease;
      z-index: 20;
    }

    :host(:hover) .click-overlay {
      opacity: 1;
    }

    :host(:hover) .click-button {
      pointer-events: auto;
    }

    /* Touch devices have no hover: keep the overlay permanently visible so
       the 3D button stays reachable. */
    @media (hover: none) {
      .click-overlay {
        opacity: 1;
      }
      .click-button {
        pointer-events: auto;
      }
    }

    .click-overlay.disabled {
      display: none;
    }

    .click-button {
      /* Only clickable while the overlay is visible — otherwise an invisible
         button floats over the material canvas. */
      pointer-events: none;
      background: rgba(0, 0, 0, 0.55);
      color: #fff;
      border: 1px solid rgba(255, 255, 255, 0.25);
      padding: 8px 16px;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 7px;
      backdrop-filter: blur(4px);
      transition: transform 0.2s ease, background 0.2s ease;
    }
    
    .click-button:hover {
      background: rgba(0, 0, 0, 0.75);
      transform: scale(1.03);
    }
    
    .click-button svg {
      width: 20px;
      height: 20px;
    }
    
    .tabs {
      position: absolute;
      bottom: 16px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      gap: 4px;
      background: rgba(0, 0, 0, 0.6);
      padding: 4px;
      border-radius: 8px;
      z-index: 15;
    }
    
    .tabs.hidden {
      display: none;
    }
    
    .tab {
      padding: 8px 16px;
      border: none;
      background: transparent;
      color: #fff;
      font-size: 13px;
      font-family: system-ui, sans-serif;
      cursor: pointer;
      border-radius: 4px;
      transition: background 0.2s ease;
    }
    
    .tab:hover {
      background: rgba(255, 255, 255, 0.1);
    }
    
    .tab.active {
      background: var(--phototron-accent, #00e5ff);
      color: #000;
    }
    
    .loading {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--phototron-bg, #050505);
      color: var(--phototron-text, #fff);
      font-family: system-ui, sans-serif;
      z-index: 25;
    }
    
    .loading.hidden {
      display: none;
    }
    
    .loading-spinner {
      width: 32px;
      height: 32px;
      border: 3px solid rgba(255, 255, 255, 0.2);
      border-top-color: var(--phototron-accent, #00e5ff);
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }
    
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    
    .error {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--phototron-bg, #050505);
      color: #ff6b6b;
      font-family: system-ui, sans-serif;
      font-size: 14px;
      padding: 20px;
      text-align: center;
      z-index: 25;
    }
    
    .error.hidden {
      display: none;
    }
  </style>
  
  <div class="container">
    <div class="loading">
      <div class="loading-spinner"></div>
    </div>
    <div class="error hidden"></div>
    
    <div class="viewer-wrapper">
      <!-- Material and Model viewers will be inserted here -->
    </div>
    
    <div class="click-overlay disabled">
      <button class="click-button">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
        <span class="click-button-label">View in 3D</span>
      </button>
    </div>
    
    <div class="tabs hidden">
      <button class="tab active" data-view="material">Material</button>
      <button class="tab" data-view="model">3D Model</button>
    </div>
  </div>
`;

export class PhototronShowcase extends HTMLElement {
  private materialViewer: HTMLElement | null = null;
  private modelViewer: HTMLElement | null = null;
  private viewerWrapper: HTMLElement | null = null;
  private clickOverlay: HTMLElement | null = null;
  private tabsEl: HTMLElement | null = null;
  private loadingEl: HTMLElement | null = null;
  private errorEl: HTMLElement | null = null;
  
  private assetData: AssetData | null = null;
  private currentView: 'material' | 'model' = 'material';
  private model3DLoaded = false;
  private _isComponentConnected = false;

  static get observedAttributes(): string[] {
    return [
      'asset-id',
      'api-key',
      'api-url',
      'manifest-url',
      'mode',
      'cta-label',
      'width',
      'height',
      // Material config
      'albedo-url',
      'normal-url',
      'specular-url',
      'roughness-url',
      'metalness-url',
      'light-intensity',
      'specular-intensity',
      // Model config
      'src',
      'auto-rotate',
      'ar-enabled',
      'camera-orbit',
      'camera-target',
      'field-of-view',
    ];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    this._isComponentConnected = true;
    this.shadowRoot!.appendChild(TEMPLATE.content.cloneNode(true));

    this.viewerWrapper = this.shadowRoot!.querySelector('.viewer-wrapper');
    this.clickOverlay = this.shadowRoot!.querySelector('.click-overlay');
    this.tabsEl = this.shadowRoot!.querySelector('.tabs');
    this.loadingEl = this.shadowRoot!.querySelector('.loading');
    this.errorEl = this.shadowRoot!.querySelector('.error');

    // Set dimensions
    this.updateDimensions();
    this.updateCtaLabel();

    // Setup event listeners
    this.setupEventListeners();

    // Initialize
    this.initialize();
  }

  disconnectedCallback(): void {
    this._isComponentConnected = false;
    this.cleanup();
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue === newValue) return;

    if (name === 'width' || name === 'height') {
      this.updateDimensions();
    }

    if (name === 'mode') {
      this.updateMode();
    }

    if (name === 'cta-label') {
      this.updateCtaLabel();
    }

    if (['asset-id', 'api-key', 'manifest-url'].includes(name)) {
      if (this._isComponentConnected) {
        this.initialize();
      }
    }
  }

  private updateDimensions(): void {
    const width = this.getAttribute('width');
    const height = this.getAttribute('height');

    if (width) {
      this.style.width = width.includes('%') || width.includes('px') ? width : `${width}px`;
    }
    if (height) {
      this.style.height = height.includes('%') || height.includes('px') ? height : `${height}px`;
    }
  }

  private updateCtaLabel(): void {
    const label = this.shadowRoot?.querySelector('.click-button-label');
    if (label) {
      label.textContent = this.getAttribute('cta-label') || 'View in 3D';
    }
  }

  private getMode(): ShowcaseMode {
    return (this.getAttribute('mode') as ShowcaseMode) || 'click-to-3d';
  }

  private setupEventListeners(): void {
    // Click overlay
    this.clickOverlay?.addEventListener('click', () => {
      this.load3DModel();
    });

    // Tab buttons
    this.tabsEl?.querySelectorAll('.tab').forEach((tab) => {
      tab.addEventListener('click', (e) => {
        const view = (e.target as HTMLElement).dataset.view as 'material' | 'model';
        this.switchView(view);
      });
    });
  }

  private updateMode(): void {
    const mode = this.getMode();

    if (mode === 'click-to-3d') {
      this.tabsEl?.classList.add('hidden');
      if (this.assetData?.model && !this.model3DLoaded) {
        this.clickOverlay?.classList.remove('disabled');
      }
    } else if (mode === 'tabs') {
      this.clickOverlay?.classList.add('disabled');
      if (this.assetData?.model) {
        this.tabsEl?.classList.remove('hidden');
      }
    }
  }

  private async initialize(): Promise<void> {
    this.showLoading();

    try {
      // Load asset data
      await this.loadAssetData();
      if (!this._isComponentConnected || !this.assetData) return;

      // Create material viewer
      this.createMaterialViewer();

      // Update mode-specific UI
      this.updateMode();

      this.hideLoading();
    } catch (error) {
      this.showError(error instanceof Error ? error.message : 'Failed to load asset');
    }
  }

  private async loadAssetData(): Promise<void> {
    // Self-describing package: a single manifest.json (AssetData) URL. Texture
    // and model paths are resolved relative to the manifest's location.
    const manifestUrl = this.getAttribute('manifest-url');
    if (manifestUrl) {
      const res = await fetch(manifestUrl);
      if (!res.ok) throw new Error(`Failed to load manifest (${res.status})`);
      const data = (await res.json()) as AssetData;
      const base = new URL(manifestUrl, location.href);
      const abs = (p?: string) => (p ? new URL(p, base).href : undefined);
      this.assetData = {
        ...data,
        textures: {
          albedo: abs(data.textures.albedo)!,
          albedoPreview: abs(data.textures.albedoPreview),
          normal: abs(data.textures.normal)!,
          normalPreview: abs(data.textures.normalPreview),
          specular: abs(data.textures.specular)!,
          specularPreview: abs(data.textures.specularPreview),
          roughness: abs(data.textures.roughness),
          roughnessPreview: abs(data.textures.roughnessPreview),
          metalness: abs(data.textures.metalness),
          metalnessPreview: abs(data.textures.metalnessPreview),
        },
        model: data.model ? { ...data.model, glb: abs(data.model.glb)! } : undefined,
      };
      return;
    }

    // Check for direct URLs
    const albedoUrl = this.getAttribute('albedo-url');
    const normalUrl = this.getAttribute('normal-url');
    const specularUrl = this.getAttribute('specular-url');
    const modelSrc = this.getAttribute('src');

    if (albedoUrl && normalUrl && specularUrl) {
      this.assetData = {
        id: 'direct',
        name: 'Direct Asset',
        textures: {
          albedo: albedoUrl,
          normal: normalUrl,
          specular: specularUrl,
          albedoPreview: this.getAttribute('albedo-preview-url') || undefined,
          normalPreview: this.getAttribute('normal-preview-url') || undefined,
          specularPreview: this.getAttribute('specular-preview-url') || undefined,
          roughness: this.getAttribute('roughness-url') || undefined,
          roughnessPreview: this.getAttribute('roughness-preview-url') || undefined,
          metalness: this.getAttribute('metalness-url') || undefined,
          metalnessPreview: this.getAttribute('metalness-preview-url') || undefined,
        },
        model: modelSrc ? { glb: modelSrc, sizeMb: 0 } : undefined,
        metadata: {
          created: new Date().toISOString(),
          dimensions: { width: 0, height: 0 },
        },
      };
      return;
    }

    // Load from API
    const assetId = this.getAttribute('asset-id');
    const apiKey = this.getAttribute('api-key');

    if (!assetId || !apiKey) {
      throw new Error('Either provide texture URLs directly or asset-id with api-key');
    }

    const apiUrl = this.getAttribute('api-url') || undefined;
    const client = new ApiClient({ apiKey, baseUrl: apiUrl });
    this.assetData = await client.getAsset(assetId);
  }

  private createMaterialViewer(): void {
    if (!this.viewerWrapper || !this.assetData) return;

    this.materialViewer = document.createElement('phototron-material');
    
    // Set texture URLs
    this.materialViewer.setAttribute('albedo-url', this.assetData.textures.albedo);
    this.materialViewer.setAttribute('normal-url', this.assetData.textures.normal);
    this.materialViewer.setAttribute('specular-url', this.assetData.textures.specular);

    if (this.assetData.textures.albedoPreview) {
      this.materialViewer.setAttribute('albedo-preview-url', this.assetData.textures.albedoPreview);
    }
    if (this.assetData.textures.normalPreview) {
      this.materialViewer.setAttribute('normal-preview-url', this.assetData.textures.normalPreview);
    }
    if (this.assetData.textures.specularPreview) {
      this.materialViewer.setAttribute('specular-preview-url', this.assetData.textures.specularPreview);
    }
    // Metallic-roughness maps (same as the GLB) — the 2D matches the 3D workflow.
    if (this.assetData.textures.roughness) {
      this.materialViewer.setAttribute('roughness-url', this.assetData.textures.roughness);
    }
    if (this.assetData.textures.roughnessPreview) {
      this.materialViewer.setAttribute('roughness-preview-url', this.assetData.textures.roughnessPreview);
    }
    if (this.assetData.textures.metalness) {
      this.materialViewer.setAttribute('metalness-url', this.assetData.textures.metalness);
    }
    if (this.assetData.textures.metalnessPreview) {
      this.materialViewer.setAttribute('metalness-preview-url', this.assetData.textures.metalnessPreview);
    }

    // Pass through lighting: showcase attribute > manifest lighting block.
    // (The inner viewer gets direct texture URLs, not the manifest-url, so the
    // manifest's display tuning has to be forwarded here.)
    const lightIntensity = this.getAttribute('light-intensity')
      ?? (this.assetData.lighting?.lightIntensity != null ? String(this.assetData.lighting.lightIntensity) : null);
    if (lightIntensity) {
      this.materialViewer.setAttribute('light-intensity', lightIntensity);
    }
    const specularIntensity = this.getAttribute('specular-intensity')
      ?? (this.assetData.lighting?.specularIntensity != null ? String(this.assetData.lighting.specularIntensity) : null);
    if (specularIntensity) {
      this.materialViewer.setAttribute('specular-intensity', specularIntensity);
    }

    this.viewerWrapper.appendChild(this.materialViewer);
  }

  private createModelViewer(): void {
    if (!this.viewerWrapper || !this.assetData?.model) return;

    this.modelViewer = document.createElement('phototron-viewer');
    this.modelViewer.setAttribute('src', this.assetData.model.glb);
    this.modelViewer.classList.add('hidden');

    // Frame the 3D model to match the 2D capture viewpoint (clean transition).
    const cameraOrbit = this.getAttribute('camera-orbit') || this.assetData.model.cameraOrbit;
    if (cameraOrbit) {
      this.modelViewer.setAttribute('camera-orbit', cameraOrbit);
    }
    const fieldOfView = this.getAttribute('field-of-view') || this.assetData.model.fieldOfView;
    if (fieldOfView) {
      this.modelViewer.setAttribute('field-of-view', fieldOfView);
    }
    const cameraTarget = this.getAttribute('camera-target') || this.assetData.model.cameraTarget;
    if (cameraTarget) {
      this.modelViewer.setAttribute('camera-target', cameraTarget);
    }

    // Pass through attributes
    const autoRotate = this.getAttribute('auto-rotate');
    if (autoRotate) {
      this.modelViewer.setAttribute('auto-rotate', autoRotate);
    }

    const arEnabled = this.getAttribute('ar-enabled');
    if (arEnabled) {
      this.modelViewer.setAttribute('ar-enabled', arEnabled);
    }

    // Listen for load event
    this.modelViewer.addEventListener('load', () => {
      this.model3DLoaded = true;
      this.switchView('model');
      this.dispatchEvent(new CustomEvent('model-loaded'));
    });

    this.viewerWrapper.appendChild(this.modelViewer);
  }

  private load3DModel(): void {
    if (!this.assetData?.model || this.model3DLoaded) return;

    // Hide click overlay
    this.clickOverlay?.classList.add('disabled');

    // Create model viewer if not exists
    if (!this.modelViewer) {
      this.createModelViewer();
    }

    this.dispatchEvent(new CustomEvent('model-loading'));
  }

  private switchView(view: 'material' | 'model'): void {
    this.currentView = view;

    if (view === 'material') {
      this.materialViewer?.classList.remove('hidden');
      this.modelViewer?.classList.add('hidden');
    } else {
      this.materialViewer?.classList.add('hidden');
      this.modelViewer?.classList.remove('hidden');

      // Load 3D model if not loaded yet
      if (!this.model3DLoaded && !this.modelViewer) {
        this.createModelViewer();
      }
    }

    // Update tab states
    this.tabsEl?.querySelectorAll('.tab').forEach((tab) => {
      const tabView = (tab as HTMLElement).dataset.view;
      tab.classList.toggle('active', tabView === view);
    });

    this.dispatchEvent(new CustomEvent('view-change', { detail: { view } }));
  }

  private showLoading(): void {
    this.loadingEl?.classList.remove('hidden');
    this.errorEl?.classList.add('hidden');
  }

  private hideLoading(): void {
    this.loadingEl?.classList.add('hidden');
  }

  private showError(message: string): void {
    this.loadingEl?.classList.add('hidden');
    if (this.errorEl) {
      this.errorEl.textContent = message;
      this.errorEl.classList.remove('hidden');
    }
  }

  private cleanup(): void {
    this.materialViewer?.remove();
    this.modelViewer?.remove();
    this.materialViewer = null;
    this.modelViewer = null;
    this.assetData = null;
    this.model3DLoaded = false;
  }

  // Public API
  public getCurrentView(): 'material' | 'model' {
    return this.currentView;
  }

  public showMaterial(): void {
    this.switchView('material');
  }

  public showModel(): void {
    this.switchView('model');
  }

  public is3DLoaded(): boolean {
    return this.model3DLoaded;
  }

  public refresh(): void {
    this.cleanup();
    this.initialize();
  }
}

// Register custom element
if (typeof customElements !== 'undefined' && !customElements.get('phototron-showcase')) {
  customElements.define('phototron-showcase', PhototronShowcase);
}
